import { supabase } from "@/integrations/supabase/client";
import { requestBillingSync } from "@/domains/billing/repository";
import { buildingDataRpcClient } from "./rpcClient";
import type { Database } from "@/integrations/supabase/types";

type BuildingRow = Database["public"]["Tables"]["buildings"]["Row"];
type BuildingInsert = Database["public"]["Tables"]["buildings"]["Insert"];
export interface ListParams {
  orgId?: string;
  search?: string;
  archived?: boolean | null;
  limit?: number;
}

const BUILDING_SUMMARY_FIELDS =
  "id,name,address,org_id,archived,modified_at,year_built,net_area,registry_code";
type BuildingSummary = Pick<
  BuildingRow,
  | "id"
  | "name"
  | "address"
  | "org_id"
  | "archived"
  | "modified_at"
  | "year_built"
  | "net_area"
  | "registry_code"
  | "subscription_plan_activation_pending"
>;

function listQuery(params: ListParams, includeActivation: boolean) {
  const { orgId, search, archived, limit = 100 } = params;
  let query = supabase
    .from("buildings")
    .select(
      includeActivation
        ? `${BUILDING_SUMMARY_FIELDS},subscription_plan_activation_pending`
        : BUILDING_SUMMARY_FIELDS
    )
    .order("modified_at", { ascending: false });

  if (orgId) query = query.eq("org_id", orgId);
  if (archived !== undefined && archived !== null) query = query.eq("archived", archived);
  if (search && search.trim()) {
    query = query.or(`name.ilike.%${search}%,address.ilike.%${search}%`);
  }

  return query.limit(limit);
}

function isMissingActivationColumn(error: { code: string; message: string }) {
  return (
    error.code === "42703" &&
    (error.message === "column buildings.subscription_plan_activation_pending does not exist" ||
      error.message === "column subscription_plan_activation_pending does not exist")
  );
}

/** Fetch summary fields through RLS, including pending activation on migrated schemas. */
export async function list(params: ListParams = {}): Promise<BuildingSummary[]> {
  const { data, error } = await listQuery(params, true);
  if (!error) return (data ?? []) as BuildingSummary[];
  if (!isMissingActivationColumn(error)) throw error;
  // Older schemas cannot create pending activations. Only this absent column
  // permits a legacy read; permission, network and other schema errors propagate.
  const legacy = await listQuery(params, false);
  if (legacy.error) throw legacy.error;
  return (legacy.data ?? []).map((building) => ({
    ...building,
    subscription_plan_activation_pending: false,
  })) as BuildingSummary[];
}

/** Return exact count of buildings, optionally filtered by org. */
export async function count(params: { orgId?: string } = {}) {
  let query = supabase.from("buildings").select("*", { count: "exact", head: true });
  if (params.orgId) query = query.eq("org_id", params.orgId);
  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

/** Get a single building by id (DB row). */
export async function getById(id: string): Promise<BuildingRow | null> {
  const numericId = Number(id);
  const { data, error } = await supabase
    .from("buildings")
    .select("*")
    .eq("id", numericId)
    .maybeSingle();
  if (error) throw error;
  return (data as BuildingRow) ?? null;
}

/** Create a building (minimal payload), returns inserted row (summary fields). */
export async function create(input: {
  name: string;
  address: string;
  cadastral_code: string;
  year_built?: number;
  net_area?: number | null;
  registry_code?: string | null;
  created_by: number;
  org_id: string;
}) {
  const now = new Date().toISOString();
  const payload: BuildingInsert = {
    name: input.name,
    address: input.address,
    cadastral_code: input.cadastral_code || null,
    year_built: input.year_built ?? new Date().getFullYear(),
    net_area: input.net_area ?? null,
    registry_code: input.registry_code ?? null,
    created_by: input.created_by,
    org_id: input.org_id,
    created_at: now,
    modified_at: now,
  };

  const { data, error } = await supabase
    .from("buildings")
    .insert(payload)
    .select("id,name,address,org_id,archived,modified_at,registry_code")
    .single();

  if (error) throw error;
  // The trigger on public.buildings has already opened the service row and
  // queued the organisation; this only asks Stripe and the billing overview to
  // catch up now rather than at the next daily sweep. Never awaited — see
  // requestBillingSync.
  requestBillingSync(String(data.org_id));
  return data as Pick<
    BuildingRow,
    "id" | "name" | "address" | "org_id" | "archived" | "modified_at" | "registry_code"
  >;
}

/** Initialize building data rows via DB RPC after building creation. */
export async function initializeBuildingData(buildingId: number) {
  const { error } = await supabase.rpc("initialize_building_data", {
    p_building_id: buildingId,
  });
  if (error) throw error;
}

/** Update building name. */
export async function updateName(id: string, name: string) {
  const numericId = Number(id);
  const { data, error } = await supabase
    .from("buildings")
    .update({ name, modified_at: new Date().toISOString() })
    .eq("id", numericId)
    .select("id,name,address,org_id,archived,modified_at")
    .single();
  if (error) throw error;
  return data as Pick<
    BuildingRow,
    "id" | "name" | "address" | "org_id" | "archived" | "modified_at"
  >;
}

/** Update arbitrary building fields. */
export async function updateFields(id: string, patch: Partial<BuildingRow>) {
  const numericId = Number(id);
  const payload = { ...patch, modified_at: new Date().toISOString() } as Partial<BuildingRow>;
  const { data, error } = await supabase
    .from("buildings")
    .update(payload)
    .eq("id", numericId)
    .select("id,name,address,org_id,archived,modified_at")
    .single();
  if (error) throw error;
  return data as Pick<
    BuildingRow,
    "id" | "name" | "address" | "org_id" | "archived" | "modified_at"
  >;
}

/** Archive or unarchive a building. */
export async function setArchived(id: string, archived: boolean, archivedBy?: number) {
  const numericId = Number(id);
  const now = new Date().toISOString();
  const patch: Partial<BuildingRow> = {
    archived,
    archived_at: archived ? now : null,
    archived_by: archived ? archivedBy : null,
    modified_at: now,
  };

  const { data, error } = await supabase
    .from("buildings")
    .update(patch)
    .eq("id", numericId)
    .select("id,name,address,org_id,archived,modified_at")
    .single();
  if (error) throw error;
  // Archiving takes a building out of the billed count and un-archiving puts it
  // back, so both move the subscription quantity. Same reasoning as create().
  requestBillingSync(String(data.org_id));
  return data as Pick<
    BuildingRow,
    "id" | "name" | "address" | "org_id" | "archived" | "modified_at"
  >;
}

export {
  rooms,
  technicalData,
  systems,
  certifications,
  structuralMaterials,
  insights,
  esg,
  budgetReports,
  lcaReports,
  fireSafety,
  finishes,
} from "./legacyRepository";

/** Save a user-entered building attribute value to data.user_data (WHA-2868). */
export async function saveUserData(params: {
  orgId: string;
  buildingId: number;
  attributeKey: string;
  attributeValue: string | null;
  editedBy: number;
}) {
  const { error } = await buildingDataRpcClient.rpc("save_user_data", {
    p_org_id: params.orgId,
    p_building_id: params.buildingId,
    p_attribute_key: params.attributeKey,
    p_attribute_value: params.attributeValue,
    p_edited_by: params.editedBy,
  });
  if (error) throw error;
}

/** Assigned users repository */
export interface AssignedUser {
  id: number;
  user_id: number;
  org_id: string;
  role: string;
  status: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone_number: string | null;
  company: string | null;
  roleId: string;
}

export const assignedUsers = {
  async list(buildingId: string, orgId: string): Promise<AssignedUser[]> {
    const bid = Number(buildingId);

    // Get users assigned to this specific building
    const { data: assignments, error: assignmentsError } = await supabase
      .from("user_building_assignments")
      .select("user_id")
      .eq("building_id", bid)
      .eq("org_id", orgId);

    if (assignmentsError) throw new Error(assignmentsError.message);

    // Extract unique user IDs
    const assignedUserIds = [...new Set(assignments?.map((a) => a.user_id) || [])];

    if (assignedUserIds.length === 0) {
      return [];
    }

    // Get roles for assigned users
    const { data: roles, error: rolesError } = await supabase
      .from("org_user_roles")
      .select("*")
      .eq("org_id", orgId)
      .in("user_id", assignedUserIds);

    if (rolesError) throw new Error(rolesError.message);

    const userIds = roles.map((r) => r.user_id);

    // Get user profiles
    const { data: profiles, error: profilesError } = await supabase
      .from("user_profiles")
      .select("id, first_name, last_name, email, phone_number, company")
      .in("id", userIds);

    if (profilesError) throw new Error(profilesError.message);

    // Map to AssignedUser type
    return roles.map((role) => {
      const profile = profiles.find((p) => p.id === role.user_id);
      return {
        id: role.user_id,
        user_id: role.user_id,
        org_id: role.org_id,
        first_name: profile?.first_name ?? null,
        last_name: profile?.last_name ?? null,
        email: profile?.email ?? "",
        phone_number: profile?.phone_number ?? null,
        company: profile?.company ?? null,
        roleId: role.id,
        role: role.role,
        status: role.status ?? "active",
        created_at: role.created_at,
      };
    });
  },
};
